import { useState, useCallback, useRef } from 'react';
import useApiCall from '../../services/api/useApiCall';
import { buildApiConfig } from '../../services/api/apiClient';
import { endPointEnums } from '../../services/api/endpoints';
import { getOrderApiConfig } from '../../utils/orderApi';
import { brandingConfig } from '@/config/branding';

// Load Razorpay script dynamically
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve(false);
      return;
    }

    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export default function usePaymentActions({
  userId,
  userPhone,
  bookingDetails,
  paymentAmount,
  onWalletUpdate,
  router,
}) {
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [createParcelLoading, setCreateParcelLoading] = useState(false);
  const [createParcelError, setCreateParcelError] = useState('');
  const [createParcelSuccess, setCreateParcelSuccess] = useState(false);
  const [lastPaymentMethod, setLastPaymentMethod] = useState(''); // Track payment method for success dialog
  const { makeApiCall } = useApiCall();

  // Prevent duplicate polling
  const pollingRef = useRef(false);

  const pollPaymentStatus = useCallback(
    async (paymentId) => {
      // Prevent concurrent polling for the same payment
      if (pollingRef.current) {
        return;
      }

      pollingRef.current = true;
      try {
        const apiConfig = buildApiConfig(endPointEnums.WALLET_PAYMENT_STATUS, {
          paymentId, // This will be added as query string to the URL
        });

        // For GET requests, buildApiConfig already built the query string into the URL
        // So we don't pass params as second argument
        const statusData = await makeApiCall(apiConfig);

        setStatus(statusData.status);
        if (['SUCCESS', 'COMPLETED'].includes(statusData.status)) {
          setError('');

          // CRITICAL FIX: Robust wallet refresh with multiple attempts
          // ensuring the balance updates even if the backend has slight lag
          const refreshWallet = async () => {
            if (onWalletUpdate) {
              await onWalletUpdate();
              // If transactions need explicit refresh, they are handled via useEffect in WalletSection
            }
            // Broadcast to all mounted useWallet instances (e.g. TopNav, other tabs)
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('walletUpdated'));
            }
          };

          // Immediate-ish refresh
          setTimeout(refreshWallet, 1000);
          // Mid-term refresh
          setTimeout(refreshWallet, 3000);
          // Final safety refresh
          setTimeout(refreshWallet, 6000);

          // Clean up URL params after successful payment
          if (typeof window !== 'undefined') {
            const url = new URL(window.location.href);
            url.searchParams.delete('orderId');
            url.searchParams.delete('amount');
            window.history.replaceState({}, '', url.toString());
          }

          return true;
        }
        if (['FAILED', 'CANCELLED'].includes(statusData.status)) {
          setError('Payment failed or cancelled.');
          return false;
        }
      } catch (err) {
        console.error('pollPaymentStatus: Error:', err);
        setError('Error fetching payment status');
        return false;
      } finally {
        pollingRef.current = false;
      }
    },
    [makeApiCall, onWalletUpdate]
  );

  const handleCheckout = async (amount) => {
    if (!userId) {
      setError('User ID is required');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) throw new Error('Failed to load Razorpay SDK');

      const apiConfig = buildApiConfig(endPointEnums.WALLET_CREATE_ORDER, {
        amount,
        customerId: userId,
        phone: String(userPhone),
        email: 'test@example.com',
      });

      const res = await makeApiCall(apiConfig);
      const data = res.data || {};

      if (data.orderId) {
        const isUpiMode = process.env.NEXT_PUBLIC_USE_UPI_INTENT === 'true';

        const options = {
          key: process.env.NEXT_PUBLIC_RAZORPAY_KEY_ID,
          amount: amount * 100, // Convert to paise
          currency: 'INR',
          name: `${brandingConfig.brandName} Wallet`,
          description: 'Wallet Top-up',
          order_id: data.orderId,
          handler: async function (response) {
            // Razorpay fires this automatically the instant UPI/card payment succeeds.
            // razorpay_payment_id is provided — no user confirmation needed.
            const paymentId = response.razorpay_payment_id;
            await new Promise((resolve) => setTimeout(resolve, 1000));
            const success = await pollPaymentStatus(paymentId);
            if (success && onWalletUpdate) await onWalletUpdate();
          },
          prefill: {
            contact: String(userPhone),
            email: 'test@example.com',
          },
          theme: { color: '#000000' },
          modal: {
            ondismiss: function () {
              setError('Payment cancelled');
              setLoading(false);
            },
          },
          // When UPI mode is enabled: restrict Razorpay checkout to UPI only.
          // Razorpay handles the QR code, app-to-app intent, and UPI collect flows
          // natively and confirms payment automatically via handler() — no UTR,
          // no user confirmation, no polling required.
          ...(isUpiMode && {
            method: { upi: 1 },
            config: {
              display: {
                blocks: {
                  upi: {
                    name: 'Pay via UPI',
                    instruments: [{ method: 'upi', flows: ['qr', 'intent', 'collect', 'sdk'] }],
                  },
                },
                sequence: ['block.upi'],
                preferences: { show_default_blocks: false },
              },
            },
          }),
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        setError('Order ID missing.');
      }
    } catch (err) {
      console.error('handleCheckout: Payment failed:', err);
      setError(err.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  const handleWalletBlockAndCreateParcel = async () => {
    setLoading(true);
    setError('');
    let walletBlocked = false;
    let blockAmount = 0;

    try {
      // 1. Block wallet amount
      const blockApiConfig = buildApiConfig(endPointEnums.WALLET_BLOCK, {
        userId,
        amount: paymentAmount,
        contextId:
          bookingDetails?.id?.toString() ||
          bookingDetails?.bulkOrderId?.toString() ||
          'parcel_payment',
      });
      const blockRes = await makeApiCall(blockApiConfig);
      if (!blockRes.success) throw new Error(blockRes.message || 'Wallet block failed');

      walletBlocked = true;
      blockAmount = paymentAmount;

      // 2. Update wallet split
      onWalletUpdate && (await onWalletUpdate());
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('walletUpdated'));
      }

      // 3. Create parcel
      setCreateParcelLoading(true);
      const config = getOrderApiConfig(bookingDetails, bookingDetails?.deliveryEstimateHours || 0);
      const parcelRes = await makeApiCall(config);
      setCreateParcelLoading(false);

      if (parcelRes && parcelRes.success) {
        setCreateParcelSuccess(true);
        // Refresh wallet one last time to reflect final balance after creation
        onWalletUpdate && onWalletUpdate();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('walletUpdated'));
        }
        setTimeout(() => {
          router.push('/activeParcels');
        }, 1500);
      } else {
        throw new Error(parcelRes?.message || 'Parcel creation failed');
      }
    } catch (err) {
      console.error('[Payment] Error:', err);
      setCreateParcelError(err.message || 'Parcel creation failed');

      // CRITICAL: Rollback wallet block if parcel creation failed
      if (walletBlocked && blockAmount > 0) {
        try {
          const unblockApiConfig = buildApiConfig(endPointEnums.WALLET_UNBLOCK, {
            userId,
            amount: blockAmount,
            contextId:
              bookingDetails?.id?.toString() ||
              bookingDetails?.bulkOrderId?.toString() ||
              'parcel_payment',
            reason: 'Parcel creation failed - automatic rollback',
          });
          const unblockRes = await makeApiCall(unblockApiConfig);
          if (unblockRes.success) {
            // Update wallet to show correct balance
            onWalletUpdate && (await onWalletUpdate());
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('walletUpdated'));
            }
          } else {
            console.error('[Payment] ROLLBACK FAILED:', unblockRes.message);
            setError('Parcel creation failed. Please contact support to unblock your wallet.');
          }
        } catch (rollbackErr) {
          console.error('[Payment] ROLLBACK ERROR:', rollbackErr);
          setError('Parcel creation failed. Please contact support to unblock your wallet.');
        }
      }
    } finally {
      setLoading(false);
      setCreateParcelLoading(false);
    }
  };

  const handleCODCreateParcel = async () => {
    setLoading(true);
    setError('');
    setCreateParcelError('');
    setCreateParcelSuccess(false);
    setLastPaymentMethod('COD'); // Track that this is a COD payment

    try {
      // 1. Create parcel with paymentType=COD (no wallet block needed)
      setCreateParcelLoading(true);
      const config = getOrderApiConfig(
        bookingDetails,
        bookingDetails?.deliveryEstimateHours || 0,
        'COD'
      );
      const parcelRes = await makeApiCall(config);
      setCreateParcelLoading(false);

      if (parcelRes && parcelRes.success) {
        // 2. Record COD transaction in payment backend
        try {
          const codConfig = buildApiConfig(endPointEnums.COD_PAYMENT, {
            contextId: String(parcelRes.data?.parcelId || parcelRes.data?.id || 'cod_parcel'),
            amount: paymentAmount,
            party_id: userId,
            party_type: 'USER',
          });
          await makeApiCall(codConfig);
        } catch (codErr) {
          // Non-blocking: parcel is already created, COD transaction logging is secondary
          console.warn('[COD] Transaction recording failed (non-blocking):', codErr);
        }

        setCreateParcelSuccess(true);
        setTimeout(() => {
          router.push('/activeParcels');
        }, 1500);
      } else {
        throw new Error(parcelRes?.message || 'Parcel creation failed');
      }
    } catch (err) {
      console.error('[COD Payment] Error:', err);
      setCreateParcelError(err.message || 'COD parcel creation failed');
    } finally {
      setLoading(false);
      setCreateParcelLoading(false);
    }
  };

  return {
    status,
    error,
    loading,
    createParcelLoading,
    createParcelError,
    createParcelSuccess,
    lastPaymentMethod,
    pollPaymentStatus,
    handleCheckout,
    handleWalletBlockAndCreateParcel,
    handleCODCreateParcel,
  };
}
